import React, { createContext, ReactElement, useContext, useReducer } from 'react';
import { none, isSome, some } from 'fp-ts/Option';
import { DashboardResult } from 'adapter/in/dashboards/context/Dashboard';
import Layout, { LayoutType } from 'domain/entity/layout';

export enum LAYOUT_STATUS {
    READY = 'READY',
    FORCE_RESET = 'FORCE_RESET',
}

export enum ACTION_TYPE {
    SET_DASHBOARD = 'SET_DASHBOARD',
    SET_SELECTED_WIDGET = 'SET_SELECTED_WIDGET',
    SET_CLICKED_WIDGET = 'SET_CLICKED_WIDGET',
    SET_DASHBOARD_NAME = 'SET_DASHBOARD_NAME',
    MODAL_DISCARD_CHANGES = 'MODAL_DISCARD_CHANGES',
    SHOW_MODAL_EDIT_NAME = 'SHOW_MODAL_EDIT_NAME',
    SHOW_SIDE_PANEL_MENU = 'SHOW_SIDE_PANEL_MENU',
    EDIT_LAYOUT_MODE = 'EDIT_LAYOUT_MODE',
    RESET = 'RESET',
    HAS_LAYOUT_CHANGES = 'HAS_LAYOUT_CHANGES',
    SET_LAYOUTS = 'SET_LAYOUTS',
    SELECT_LAYOUT_TYPE = 'SELECT_LAYOUT_TYPE',
    SET_LAYOUT_STATUS = 'SET_LAYOUT_STATUS',
}

export const initialState: DashboardEditViewState = {
    dashboard: none,
    modalDiscardChanges: {
        show: false,
        viewCleanupAction: undefined,
    },
    showModalEditName: false,
    showSidePanelMenu: false,
    editLayoutMode: false,
    hasLayoutChanges: false,
    layouts: new Map(),
    selectedLayoutType: LayoutType.DESKTOP,
    layoutStatus: LAYOUT_STATUS.READY,
};

type SetDashboard = {
    type: ACTION_TYPE.SET_DASHBOARD;
    payload: DashboardResult;
};

type SetDashboardName = {
    type: ACTION_TYPE.SET_DASHBOARD_NAME;
    payload: string;
};

type ModalDiscardChanges = {
    type: ACTION_TYPE.MODAL_DISCARD_CHANGES;
    payload: ModalDiscardChangesState;
};

type SetShowModalEditName = {
    type: ACTION_TYPE.SHOW_MODAL_EDIT_NAME;
    payload: boolean;
};

type SetShowSidePanelMenu = {
    type: ACTION_TYPE.SHOW_SIDE_PANEL_MENU;
    payload: boolean;
};

type SetEditLayoutMode = {
    type: ACTION_TYPE.EDIT_LAYOUT_MODE;
    payload: boolean;
};

type SelectLayout = {
    type: ACTION_TYPE.SELECT_LAYOUT_TYPE;
    payload: LayoutType;
};

type Reset = {
    type: ACTION_TYPE.RESET;
    payload: null;
};

// LAYOUT CONTEXT PROPS
type SetHasLayoutChanges = {
    type: ACTION_TYPE.HAS_LAYOUT_CHANGES;
    payload: boolean;
};

type SetLayouts = {
    type: ACTION_TYPE.SET_LAYOUTS;
    payload: Map<LayoutType, Layout>;
};

type SetLayoutStatus = {
    type: ACTION_TYPE.SET_LAYOUT_STATUS;
    payload: LAYOUT_STATUS;
};

export type DashboardEditViewState = {
    dashboard: DashboardResult;
    modalDiscardChanges: ModalDiscardChangesState;
    showModalEditName: boolean;
    showSidePanelMenu: boolean;
    editLayoutMode: boolean;
    hasLayoutChanges: boolean;
    layouts: Map<LayoutType, Layout>;
    selectedLayoutType: LayoutType;
    layoutStatus: LAYOUT_STATUS;
};

export type ModalDiscardChangesState = {
    show: boolean;
    viewCleanupAction?: () => void;
    saveAndProceedAction?: () => void;
};

export type DashboardEditViewDispatch = {
    setDashboard: (dashboard: DashboardResult) => void;
    setDashboardNameAndCloseModal: (name: string) => void;
    setModalDiscardChanges: (show: boolean, viewCleanupAction?: () => void, saveAndProceedAction?: () => void) => void;
    setShowModalEditName: (showModalEditName: boolean) => void;
    setShowSidePanelMenu: (showSidePanelMenu: boolean) => void;
    setEditLayoutMode: (editLayoutMode: boolean) => void;
    setHasLayoutChanges: (hasLayoutChanges: boolean) => void;
    setLayouts: (layouts: Map<LayoutType, Layout>) => void;
    selectLayoutType: (layoutType: LayoutType) => void;
    setLayoutStatus: (status: LAYOUT_STATUS) => void;
    reset: () => void;
};

type DashboardEditViewAction =
    | SetDashboard
    | SetDashboardName
    | ModalDiscardChanges
    | SetShowModalEditName
    | SetShowSidePanelMenu
    | SetEditLayoutMode
    | SetHasLayoutChanges
    | SetLayouts
    | SelectLayout
    | SetLayoutStatus
    | Reset;

export const useCaseEditDashboardReducer = (
    state: DashboardEditViewState,
    action: DashboardEditViewAction
    // eslint-disable-next-line consistent-return
): DashboardEditViewState => {
    const { type, payload } = action;
    // eslint-disable-next-line default-case
    switch (type) {
        case ACTION_TYPE.SET_DASHBOARD: {
            return {
                ...state,
                dashboard: payload,
            };
        }
        case ACTION_TYPE.SET_DASHBOARD_NAME: {
            return {
                ...state,
                dashboard: {
                    ...(isSome(state.dashboard) ? some(state.dashboard.value.copyWith({ name: payload })) : state.dashboard),
                },
                showModalEditName: false,
            };
        }

        case ACTION_TYPE.MODAL_DISCARD_CHANGES: {
            return {
                ...state,
                modalDiscardChanges: payload,
            };
        }

        case ACTION_TYPE.SHOW_MODAL_EDIT_NAME: {
            return {
                ...state,
                showModalEditName: payload,
            };
        }

        case ACTION_TYPE.SHOW_SIDE_PANEL_MENU: {
            return {
                ...state,
                showSidePanelMenu: payload,
                modalDiscardChanges: {
                    ...state.modalDiscardChanges,
                    show: false,
                },
                showModalEditName: false,
            };
        }

        case ACTION_TYPE.EDIT_LAYOUT_MODE: {
            return {
                ...state,
                editLayoutMode: payload,
            };
        }

        case ACTION_TYPE.RESET: {
            return { ...initialState };
        }

        case ACTION_TYPE.HAS_LAYOUT_CHANGES: {
            return { ...state, hasLayoutChanges: payload };
        }

        case ACTION_TYPE.SET_LAYOUTS: {
            return { ...state, layouts: payload };
        }

        case ACTION_TYPE.SELECT_LAYOUT_TYPE: {
            return {
                ...state,
                selectedLayoutType: payload,
            };
        }

        case ACTION_TYPE.SET_LAYOUT_STATUS: {
            return {
                ...state,
                layoutStatus: payload,
            };
        }
    }
};

type DashboardEditProviderType = {
    children: ReactElement;
};

export const DashboardEditViewDispatchContext = createContext({} as DashboardEditViewDispatch);
export const DashboardEditViewStateContext = createContext(initialState);

const dispatchDashboardEdit = (): DashboardEditViewDispatch => useContext(DashboardEditViewDispatchContext);
const useDashboardEdit = (): DashboardEditViewState => useContext(DashboardEditViewStateContext);

const DashboardEditProvider = (props: DashboardEditProviderType): ReactElement => {
    const { children } = props;
    const [state, dispatch] = useReducer(useCaseEditDashboardReducer, initialState);

    const setDashboard = (dashboard: DashboardResult) =>
        dispatch({
            type: ACTION_TYPE.SET_DASHBOARD,
            payload: dashboard,
        });

    const setDashboardNameAndCloseModal = (name: string) =>
        dispatch({
            type: ACTION_TYPE.SET_DASHBOARD_NAME,
            payload: name,
        });

    const setShowModalEditName = (showModalEditName: boolean) =>
        dispatch({
            type: ACTION_TYPE.SHOW_MODAL_EDIT_NAME,
            payload: showModalEditName,
        });

    const setModalDiscardChanges = (show: boolean, viewCleanupAction?: () => void, saveAndProceedAction?: () => void) =>
        dispatch({
            type: ACTION_TYPE.MODAL_DISCARD_CHANGES,
            payload: {
                show,
                viewCleanupAction,
                saveAndProceedAction,
            },
        });

    const setShowSidePanelMenu = (showExitPanelMenu: boolean) =>
        dispatch({
            type: ACTION_TYPE.SHOW_SIDE_PANEL_MENU,
            payload: showExitPanelMenu,
        });

    const setEditLayoutMode = (editLayoutMode: boolean) =>
        dispatch({
            type: ACTION_TYPE.EDIT_LAYOUT_MODE,
            payload: editLayoutMode,
        });

    const reset = () =>
        dispatch({
            type: ACTION_TYPE.RESET,
            payload: null,
        });

    const setHasLayoutChanges = (hasLayoutChanges: boolean) =>
        dispatch({
            type: ACTION_TYPE.HAS_LAYOUT_CHANGES,
            payload: hasLayoutChanges,
        });

    const setLayouts = (layouts: Map<LayoutType, Layout>) =>
        dispatch({
            type: ACTION_TYPE.SET_LAYOUTS,
            payload: layouts,
        });

    const selectLayoutType = (layoutType: LayoutType) =>
        dispatch({
            type: ACTION_TYPE.SELECT_LAYOUT_TYPE,
            payload: layoutType,
        });

    const setLayoutStatus = (layoutStatus: LAYOUT_STATUS) =>
        dispatch({
            type: ACTION_TYPE.SET_LAYOUT_STATUS,
            payload: layoutStatus,
        });

    return (
        <DashboardEditViewStateContext.Provider value={state}>
            <DashboardEditViewDispatchContext.Provider
                value={{
                    setDashboard,
                    setDashboardNameAndCloseModal,
                    setModalDiscardChanges,
                    setShowModalEditName,
                    setShowSidePanelMenu,
                    setEditLayoutMode,
                    reset,
                    setHasLayoutChanges,
                    setLayouts,
                    selectLayoutType,
                    setLayoutStatus,
                }}
            >
                {children}
            </DashboardEditViewDispatchContext.Provider>
        </DashboardEditViewStateContext.Provider>
    );
};

export { DashboardEditProvider, dispatchDashboardEdit, useDashboardEdit };
